import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { config } from '../../config/environment';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { 
  Layers, 
  Plus, 
  Trash2, 
  RefreshCw, 
  DollarSign,
  Package,
  Calculator,
  AlertCircle,
  Lock,
  Unlock,
  Save
} from 'lucide-react';

const API = config.apiBaseUrl;
const MOCK_TENANT_ID = 'tenant-123';

// Unit options for BOM components
const UNIT_OPTIONS = [
  { value: 'pcs', label: 'pcs' },
  { value: 'oz', label: 'oz' },
  { value: 'lb', label: 'lb' },
  { value: 'g', label: 'g' },
  { value: 'kg', label: 'kg' },
  { value: 'ml', label: 'ml' },
  { value: 'L', label: 'L' }
];

const BOMManager = ({ 
  productId, 
  productType = 'finished_product',
  onCostUpdate = () => {},
  readOnly = false 
}) => {
  const [bom, setBom] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [costingData, setCostingData] = useState({
    costing_mode: 'auto',
    component_cost: 0,
    fulfillment_cost: 0,
    total_cogs: 0,
    suggested_wholesale_price: 0,
    suggested_retail_price: 0,
    wholesale_price: 0,
    retail_price: 0,
    wholesale_price_mode: 'auto',
    retail_price_mode: 'auto'
  });
  const [newItem, setNewItem] = useState({ component_sku: '', qty: 1, unit: 'pcs' });
  const [showAddForm, setShowAddForm] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch BOM data
  const fetchBOM = useCallback(async () => {
    if (!productId) return;
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/products/${productId}/bom`, {
        params: { tenant_id: MOCK_TENANT_ID },
        headers: { 'X-Tenant-ID': MOCK_TENANT_ID }
      });
      
      if (response.data.success) {
        setBom(response.data.bom || []);
        setCostingData({
          costing_mode: response.data.costing_mode || 'auto',
          component_cost: response.data.component_cost || 0,
          fulfillment_cost: response.data.fulfillment_cost || 0,
          total_cogs: response.data.total_cogs || 0,
          suggested_wholesale_price: response.data.suggested_wholesale_price || 0,
          suggested_retail_price: response.data.suggested_retail_price || 0,
          wholesale_price: response.data.wholesale_price || 0,
          retail_price: response.data.retail_price || 0,
          wholesale_price_mode: response.data.wholesale_price_mode || 'auto',
          retail_price_mode: response.data.retail_price_mode || 'auto'
        });
      }
    } catch (error) {
      console.error('Error fetching BOM:', error);
      toast.error('Failed to load BOM data');
    } finally {
      setLoading(false);
    }
  }, [productId]);

  // Fetch available raw materials for dropdown
  const fetchRawMaterials = useCallback(async (search = '') => {
    try {
      const response = await axios.get(`${API}/products/raw-materials-for-bom`, {
        params: { tenant_id: MOCK_TENANT_ID, search },
        headers: { 'X-Tenant-ID': MOCK_TENANT_ID }
      });
      
      if (response.data.success) {
        setRawMaterials(response.data.raw_materials || []);
      }
    } catch (error) {
      console.error('Error fetching raw materials:', error);
    }
  }, []);

  useEffect(() => {
    fetchBOM();
    fetchRawMaterials();
  }, [fetchBOM, fetchRawMaterials]);

  // Handle search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchRawMaterials(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, fetchRawMaterials]);

  // Add BOM item
  const handleAddItem = async () => {
    if (!newItem.component_sku) {
      toast.error('Please select a component');
      return;
    }

    try {
      setSaving(true);
      const response = await axios.post(
        `${API}/products/${productId}/bom/add`,
        null,
        {
          params: {
            tenant_id: MOCK_TENANT_ID,
            component_sku: newItem.component_sku,
            qty: newItem.qty,
            unit: newItem.unit || 'pcs'
          },
          headers: { 'X-Tenant-ID': MOCK_TENANT_ID }
        }
      );

      if (response.data.success) {
        setBom(response.data.bom);
        setNewItem({ component_sku: '', qty: 1, unit: 'pcs' });
        setShowAddForm(false);
        toast.success('Component added to BOM');
        fetchBOM(); // Refresh to get updated costs
        onCostUpdate();
      }
    } catch (error) {
      console.error('Error adding BOM item:', error);
      toast.error(error.response?.data?.detail || 'Failed to add component');
    } finally {
      setSaving(false);
    }
  };

  // Remove BOM item
  const handleRemoveItem = async (componentSku) => {
    try {
      setSaving(true);
      const response = await axios.delete(
        `${API}/products/${productId}/bom/${encodeURIComponent(componentSku)}`,
        {
          params: { tenant_id: MOCK_TENANT_ID },
          headers: { 'X-Tenant-ID': MOCK_TENANT_ID }
        }
      );

      if (response.data.success) {
        setBom(response.data.bom);
        toast.success('Component removed from BOM');
        fetchBOM(); // Refresh to get updated costs
        onCostUpdate();
      }
    } catch (error) {
      console.error('Error removing BOM item:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove component');
    } finally {
      setSaving(false);
    }
  };

  // Update quantity inline
  const handleUpdateQty = (componentSku, newQty) => {
    setBom(prev => prev.map(item => 
      item.component_sku === componentSku 
        ? { ...item, qty: parseFloat(newQty) || 0 }
        : item
    ));
    setHasChanges(true);
  };

  // Update unit inline
  const handleUpdateUnit = (componentSku, newUnit) => {
    setBom(prev => prev.map(item => 
      item.component_sku === componentSku 
        ? { ...item, unit: newUnit }
        : item
    ));
    setHasChanges(true);
  };

  // Save all BOM changes
  const handleSaveBOM = async () => {
    try {
      setSaving(true);
      const response = await axios.put(
        `${API}/products/${productId}/bom`,
        { 
          bom: bom.map(item => ({
            component_sku: item.component_sku,
            qty: item.qty,
            unit: item.unit || 'pcs'  // Include unit, default to pcs
          })),
          recalculate: true
        },
        {
          params: { tenant_id: MOCK_TENANT_ID },
          headers: { 'X-Tenant-ID': MOCK_TENANT_ID }
        }
      );

      if (response.data.success) {
        toast.success('BOM saved and costs recalculated');
        setHasChanges(false);
        fetchBOM(); // Refresh to get updated costs
        onCostUpdate();
      }
    } catch (error) {
      console.error('Error saving BOM:', error);
      toast.error(error.response?.data?.detail || 'Failed to save BOM');
    } finally {
      setSaving(false);
    }
  };

  // Recalculate costs
  const handleRecalculate = async () => {
    try {
      setSaving(true);
      const response = await axios.post(
        `${API}/products/${productId}/recalculate-costs`,
        null,
        {
          params: { tenant_id: MOCK_TENANT_ID },
          headers: { 'X-Tenant-ID': MOCK_TENANT_ID }
        }
      );

      if (response.data.success) {
        toast.success('Costs recalculated');
        fetchBOM();
        onCostUpdate();
      }
    } catch (error) {
      console.error('Error recalculating:', error);
      toast.error('Failed to recalculate costs');
    } finally {
      setSaving(false);
    }
  };

  // Toggle price mode
  const handleTogglePriceMode = async (priceType) => {
    const modeField = `${priceType}_price_mode`;
    const newMode = costingData[modeField] === 'auto' ? 'manual' : 'auto';
    
    try {
      const response = await axios.patch(
        `${API}/products/${productId}/costing`,
        { [modeField]: newMode },
        {
          params: { tenant_id: MOCK_TENANT_ID },
          headers: { 'X-Tenant-ID': MOCK_TENANT_ID }
        }
      );

      if (response.data.success) {
        setCostingData(prev => ({ ...prev, [modeField]: newMode }));
        toast.success(`${priceType === 'wholesale' ? 'Wholesale' : 'Retail'} price mode: ${newMode.toUpperCase()}`);
        fetchBOM();
      }
    } catch (error) {
      console.error('Error toggling price mode:', error);
      toast.error('Failed to update price mode');
    }
  };

  // Calculate totals
  const totalComponentCost = bom.reduce((sum, item) => sum + (item.extended_cost || 0), 0);

  if (productType === 'raw_material') {
    return (
      <div className="p-4 text-center">
        <Package className="w-12 h-12 text-gray-500 mx-auto mb-3" />
        <h3 className="text-sm font-medium text-gray-300 mb-1">Raw Material</h3>
        <p className="text-xs text-gray-500">
          Raw materials do not have a Bill of Materials. 
          BOM is only applicable to finished products.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-5 h-5 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            <Layers className="w-4 h-4 text-purple-400" />
            Bill of Materials
          </h3>
          <p className="text-xs text-gray-400 mt-0.5">
            Components that make up this finished product
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && (
            <Button
              size="sm"
              onClick={handleSaveBOM}
              disabled={saving}
              className="h-7 text-xs bg-green-600 hover:bg-green-700"
              data-testid="save-bom-btn"
            >
              <Save className="w-3 h-3 mr-1" />
              Save Changes
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleRecalculate}
            disabled={saving}
            className="h-7 text-xs"
            data-testid="recalculate-costs-btn"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${saving ? 'animate-spin' : ''}`} />
            Recalculate
          </Button>
          {!readOnly && (
            <Button
              size="sm"
              onClick={() => setShowAddForm(true)}
              disabled={saving}
              className="h-7 text-xs"
              data-testid="add-component-btn"
            >
              <Plus className="w-3 h-3 mr-1" />
              Add Component
            </Button>
          )}
        </div>
      </div>

      {/* Add Component Form */}
      {showAddForm && (
        <Card className="bg-gray-800/50 border-gray-700">
          <CardContent className="p-3">
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Label className="text-xs text-gray-400 mb-1 block">Search Raw Material</Label>
                <Input
                  placeholder="Search by SKU or name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-8 text-xs bg-gray-700 border-gray-600"
                  data-testid="search-raw-material-input"
                />
                {rawMaterials.length > 0 && searchQuery && (
                  <div className="mt-1 max-h-40 overflow-y-auto bg-gray-700 border border-gray-600 rounded">
                    {rawMaterials.map((rm) => (
                      <button
                        key={rm.sku}
                        onClick={() => {
                          setNewItem(prev => ({ ...prev, component_sku: rm.sku }));
                          setSearchQuery(rm.label);
                        }}
                        className="w-full px-2 py-1.5 text-left text-xs hover:bg-gray-600 flex justify-between"
                        data-testid={`raw-material-option-${rm.sku}`}
                      >
                        <span className="text-white">{rm.sku}</span>
                        <span className="text-gray-400">${rm.landed_cost.toFixed(2)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="w-24">
                <Label className="text-xs text-gray-400 mb-1 block">Qty</Label>
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={newItem.qty}
                  onChange={(e) => setNewItem(prev => ({ ...prev, qty: parseFloat(e.target.value) || 1 }))}
                  className="h-8 text-xs bg-gray-700 border-gray-600"
                  data-testid="component-qty-input"
                />
              </div>
              <div className="w-20">
                <Label className="text-xs text-gray-400 mb-1 block">Unit</Label>
                <select
                  value={newItem.unit}
                  onChange={(e) => setNewItem(prev => ({ ...prev, unit: e.target.value }))}
                  className="h-8 w-full text-xs bg-gray-700 border border-gray-600 rounded-md text-white px-2"
                  data-testid="component-unit-select"
                >
                  {UNIT_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <Button
                size="sm"
                onClick={handleAddItem}
                disabled={!newItem.component_sku || saving}
                className="h-8 text-xs"
                data-testid="confirm-add-component-btn"
              >
                Add
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowAddForm(false);
                  setNewItem({ component_sku: '', qty: 1, unit: 'pcs' });
                  setSearchQuery('');
                }}
                className="h-8 text-xs"
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* BOM Table */}
      {bom.length > 0 ? (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-0">
            <table className="w-full">
              <thead className="bg-gray-700/50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-400">Component SKU</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-400">Name</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-400">Qty</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-400">Unit Cost</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-400">Extended</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-400 w-16">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {bom.map((item) => (
                  <tr key={item.component_sku} className="hover:bg-gray-700/30">
                    <td className="px-3 py-2">
                      <code className="text-xs text-blue-400 bg-blue-900/30 px-1.5 py-0.5 rounded">
                        {item.component_sku}
                      </code>
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-300">
                      {item.component_name || '-'}
                    </td>
                    <td className="px-3 py-2">
                      {readOnly ? (
                        <span className="text-xs text-white flex items-center justify-end gap-1">
                          {item.qty} <span className="text-gray-400">{item.unit || 'pcs'}</span>
                        </span>
                      ) : (
                        <div className="flex items-center justify-end gap-1">
                          <Input
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={item.qty}
                            onChange={(e) => handleUpdateQty(item.component_sku, e.target.value)}
                            className="w-16 h-7 text-xs text-right bg-gray-700 border-gray-600"
                            data-testid={`bom-qty-${item.component_sku}`}
                          />
                          <select
                            value={item.unit || 'pcs'}
                            onChange={(e) => handleUpdateUnit(item.component_sku, e.target.value)}
                            className="h-7 text-xs bg-gray-700 border border-gray-600 rounded text-white px-1"
                            data-testid={`bom-unit-${item.component_sku}`}
                          >
                            {UNIT_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-xs text-gray-300">
                      ${(item.unit_landed_cost || 0).toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-right text-xs text-green-400 font-medium">
                      ${(item.extended_cost || 0).toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {!readOnly && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveItem(item.component_sku)}
                          disabled={saving}
                          className="h-6 w-6 p-0 text-red-400 hover:text-red-300 hover:bg-red-900/30"
                          data-testid={`remove-bom-${item.component_sku}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-700/30">
                <tr>
                  <td colSpan="4" className="px-3 py-2 text-right text-xs font-medium text-gray-300">
                    Component Cost:
                  </td>
                  <td className="px-3 py-2 text-right text-sm text-blue-400 font-semibold">
                    ${totalComponentCost.toFixed(2)}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </CardContent>
        </Card>
      ) : (
        <div className="text-center py-8 bg-gray-800/30 rounded-lg border border-gray-700 border-dashed">
          <Layers className="w-10 h-10 text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-400">No components in BOM</p>
          <p className="text-xs text-gray-500 mt-1">Click &quot;Add Component&quot; to build the bill of materials</p>
        </div>
      )}

      {/* Cost Summary */}
      {bom.length > 0 && (
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader className="p-3 pb-2">
            <CardTitle className="text-xs font-medium text-gray-300 flex items-center gap-2">
              <Calculator className="w-3.5 h-3.5 text-green-400" />
              Cost Calculation Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-0">
            <div className="grid grid-cols-2 gap-4">
              {/* Cost Breakdown */}
              <div className="space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-400">Component Cost:</span>
                  <span className="text-white font-mono">${costingData.component_cost.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-400">+ Fulfillment Cost:</span>
                  <span className="text-green-400 font-mono">+${costingData.fulfillment_cost.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xs border-t border-gray-700 pt-2">
                  <span className="text-white font-medium">Total COGS:</span>
                  <span className="text-blue-400 font-mono font-semibold">${costingData.total_cogs.toFixed(2)}</span>
                </div>
              </div>

              {/* Suggested Prices */}
              <div className="space-y-2">
                <div className="flex justify-between items-center text-xs">
                  <div className="flex items-center gap-1.5">
                    <span className="text-gray-400">Wholesale:</span>
                    <button
                      onClick={() => handleTogglePriceMode('wholesale')}
                      className="text-gray-500 hover:text-gray-300"
                      title={`Mode: ${costingData.wholesale_price_mode}`}
                    >
                      {costingData.wholesale_price_mode === 'auto' ? (
                        <Unlock className="w-3 h-3" />
                      ) : (
                        <Lock className="w-3 h-3 text-yellow-500" />
                      )}
                    </button>
                  </div>
                  <div className="text-right">
                    <span className="text-purple-400 font-mono">${costingData.wholesale_price.toFixed(2)}</span>
                    {costingData.wholesale_price_mode === 'auto' && (
                      <span className="text-gray-500 text-[10px] ml-1">(suggested: ${costingData.suggested_wholesale_price.toFixed(2)})</span>
                    )}
                  </div>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <div className="flex items-center gap-1.5">
                    <span className="text-gray-400">Retail:</span>
                    <button
                      onClick={() => handleTogglePriceMode('retail')}
                      className="text-gray-500 hover:text-gray-300"
                      title={`Mode: ${costingData.retail_price_mode}`}
                    >
                      {costingData.retail_price_mode === 'auto' ? (
                        <Unlock className="w-3 h-3" />
                      ) : (
                        <Lock className="w-3 h-3 text-yellow-500" />
                      )}
                    </button>
                  </div>
                  <div className="text-right">
                    <span className="text-purple-400 font-mono">${costingData.retail_price.toFixed(2)}</span>
                    {costingData.retail_price_mode === 'auto' && (
                      <span className="text-gray-500 text-[10px] ml-1">(suggested: ${costingData.suggested_retail_price.toFixed(2)})</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Mode indicator */}
            <div className="mt-3 pt-2 border-t border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge variant={costingData.costing_mode === 'auto' ? 'default' : 'secondary'} className="text-[10px]">
                  {costingData.costing_mode === 'auto' ? 'Auto Costing' : 'Manual Costing'}
                </Badge>
              </div>
              <p className="text-[10px] text-gray-500">
                {costingData.costing_mode === 'auto' 
                  ? 'Costs auto-update when control sheet or BOM changes'
                  : 'Costs are manually managed'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BOMManager;
